// Shell del módulo Mantenimiento:
//   - RequireMantenimiento: guard de la capability `puede_ver_mantenimiento`
//     (si no la tiene → redirect a su home efectivo; el gate duro es el API).
//   - MantenimientoShell: layout del rol (sidebar según rol de plataforma) +
//     header del módulo con sub-navegación por tabs (Panel / Órdenes / Activos
//     / Repuestos / Informes).
//   - Personal DEDICADO a mantenimiento (rol 'lider' + rol de módulo, decisión
//     2026-07-05): NO monta el shell de su rol de producción — el módulo es su
//     mundo, con sidebar propio (MantenimientoSidebar) y sin tabs redundantes.

import type { ReactNode } from 'react';
import { Navigate, NavLink } from 'react-router-dom';
import { AdminSidebar } from '../../components/layout/AdminSidebar';
import { CoordinadorSidebar } from '../../components/layout/CoordinadorSidebar';
import { DirectivoLayout } from '../../components/layout/DirectivoLayout';
import { MantenimientoSidebar } from '../../components/layout/MantenimientoSidebar';
import { RoleLayout } from '../../components/layout/RoleLayout';
import { Spinner } from '../../components/ui/spinner';
import { useMe } from '../../lib/api/queries';
import { useAuth, useHomePath, useMantenimientoDedicado } from '../../lib/auth';
import { cn } from '../../lib/utils';
import { NotificacionesCampana } from './NotificacionesCampana';
import { usePermisosMtto } from './shared';

// ---------------------------------------------------------------------------
// Guard
// ---------------------------------------------------------------------------

export function RequireMantenimiento({ children }: { children: ReactNode }) {
  const me = useMe();
  // Home EFECTIVO (rol + regla de dedicado). Como la regla de dedicado exige
  // la capability, en la rama sin capability el home NUNCA es /mantenimiento
  // — imposible el loop de redirects.
  const homePath = useHomePath();

  if (me.isLoading) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-3 bg-surface-base p-6 text-fg">
        <Spinner className="size-6" />
        <p className="text-sm text-fg-secondary">Cargando mantenimiento…</p>
      </main>
    );
  }

  if (me.isError || !me.data) {
    return (
      <main className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-3 bg-surface-base p-6 text-center text-fg">
        <h1 className="text-lg font-medium text-crit">No pudimos cargar</h1>
        <p className="text-sm text-fg-secondary">
          {me.error instanceof Error ? me.error.message : 'Error desconocido'}
        </p>
      </main>
    );
  }

  if (!me.data.ctx.puede_ver_mantenimiento) {
    return <Navigate to={homePath.home} replace />;
  }

  return <>{children}</>;
}

// ---------------------------------------------------------------------------
// Guards de sub-sección — bloqueo DURO por ruta (no solo ocultar del nav).
//   - Análisis de paros / Informes: vistas de gestión (incluyen $ y KPIs de
//     decisión) → el técnico de piso queda fuera.
//   - Personal: gestión de usuarios del módulo → solo coordinador mtto/admin.
// El técnico que teclee la URL a mano es redirigido al panel del módulo.
// El gate DURO real sigue siendo el API; esto es coherencia de navegación.
// ---------------------------------------------------------------------------

function RequirePermisoMtto({
  permitido,
  children,
}: {
  permitido: (p: ReturnType<typeof usePermisosMtto>) => boolean;
  children: ReactNode;
}) {
  const me = useMe();
  const permisos = usePermisosMtto();

  if (me.isLoading) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-3 bg-surface-base p-6 text-fg">
        <Spinner className="size-6" />
        <p className="text-sm text-fg-secondary">Cargando mantenimiento…</p>
      </main>
    );
  }
  if (me.isError || !me.data) {
    return <Navigate to="/mantenimiento" replace />;
  }
  if (!permitido(permisos)) {
    return <Navigate to="/mantenimiento" replace />;
  }
  return <>{children}</>;
}

/** Análisis de paros e Informes — vistas de gestión (incluyen valores $). */
export function RequireMttoAnalisis({ children }: { children: ReactNode }) {
  return <RequirePermisoMtto permitido={(p) => p.puedeVerAnalisis}>{children}</RequirePermisoMtto>;
}

/** Pestaña Personal — gestión de usuarios del módulo. */
export function RequireMttoGestionPersonal({ children }: { children: ReactNode }) {
  return (
    <RequirePermisoMtto permitido={(p) => p.puedeGestionarPersonal}>{children}</RequirePermisoMtto>
  );
}

// ---------------------------------------------------------------------------
// Shell (layout por rol + sub-nav del módulo)
// ---------------------------------------------------------------------------

const MODULO_NAV = [
  { to: '/mantenimiento', label: 'Panel', end: true },
  { to: '/mantenimiento/lineas', label: 'Líneas y paros', end: false },
  { to: '/mantenimiento/paros', label: 'Análisis de paros', end: false, soloAnalisis: true },
  { to: '/mantenimiento/ordenes', label: 'Órdenes de trabajo', end: false },
  { to: '/mantenimiento/activos', label: 'Activos', end: false },
  { to: '/mantenimiento/repuestos', label: 'Repuestos', end: false },
  { to: '/mantenimiento/programacion', label: 'Programación', end: false },
  { to: '/mantenimiento/personal', label: 'Personal', end: false, soloGestion: true },
  { to: '/mantenimiento/informes', label: 'Informes', end: false, soloAnalisis: true },
] as const;

function ModuloNav() {
  const { puedeGestionarPersonal, puedeVerAnalisis } = usePermisosMtto();
  const items = MODULO_NAV.filter((item) => {
    if ('soloGestion' in item && item.soloGestion && !puedeGestionarPersonal) return false;
    if ('soloAnalisis' in item && item.soloAnalisis && !puedeVerAnalisis) return false;
    return true;
  });
  return (
    <nav
      aria-label="Secciones de mantenimiento"
      className="flex items-stretch gap-1 overflow-x-auto border-b border-border"
    >
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          className={({ isActive }) =>
            cn(
              '-mb-px whitespace-nowrap px-4 py-2 text-sm transition-colors',
              isActive
                ? 'border-b-2 border-accent font-medium text-fg'
                : 'border-b-2 border-transparent text-fg-secondary hover:text-fg',
            )
          }
        >
          {item.label}
        </NavLink>
      ))}
    </nav>
  );
}

interface MantenimientoShellProps {
  /** Título de la pantalla dentro del módulo (ej. "Órdenes de trabajo"). */
  titulo: string;
  /** Subtítulo informativo bajo el título. */
  subtitulo?: ReactNode;
  /** Acciones top-right del header (ej. botón "Crear orden"). */
  acciones?: ReactNode;
  children: ReactNode;
}

export function MantenimientoShell({
  titulo,
  subtitulo,
  acciones,
  children,
}: MantenimientoShellProps) {
  const { state } = useAuth();
  const dedicado = useMantenimientoDedicado();
  const rol = state.kind === 'signed_in' ? state.claims.rol : null;

  const contenido = (
    <div className="flex flex-col gap-4">
      <header className="flex flex-col gap-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-medium text-fg">{titulo}</h1>
            {subtitulo ? <p className="mt-1 text-[11px] text-fg-tertiary">{subtitulo}</p> : null}
          </div>
          <div className="flex items-center gap-2">
            {acciones}
            <NotificacionesCampana />
          </div>
        </div>
        {/* Para el dedicado los tabs son redundantes: su sidebar YA es la
            navegación del módulo. */}
        {dedicado ? null : <ModuloNav />}
      </header>
      {children}
    </div>
  );

  // Personal DEDICADO: el módulo es su mundo — sidebar propio, sin el shell
  // de producción de su rol base ('lider' no tiene nav de mantenimiento).
  if (dedicado) {
    return (
      <RoleLayout sidebar={<MantenimientoSidebar />} roleLabel="Mantenimiento">
        {contenido}
      </RoleLayout>
    );
  }

  // Roles con mundo propio: el módulo vive dentro del shell del rol para que
  // no se sienta un injerto — mismo header, mismo sidebar, misma vuelta.
  if (rol === 'directivo') {
    return <DirectivoLayout>{contenido}</DirectivoLayout>;
  }
  if (rol === 'admin_tenant') {
    return (
      <RoleLayout sidebar={<AdminSidebar />} roleLabel="Admin del Tenant">
        {contenido}
      </RoleLayout>
    );
  }
  return (
    <RoleLayout sidebar={<CoordinadorSidebar />} roleLabel="Coordinador">
      {contenido}
    </RoleLayout>
  );
}

// ---------------------------------------------------------------------------
// Estados de carga/error consistentes con el resto de la app
// ---------------------------------------------------------------------------

export function CargandoModulo({ mensaje }: { mensaje: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-16">
      <Spinner className="size-6" />
      <p className="text-sm text-fg-secondary">{mensaje}</p>
    </div>
  );
}

export function ErrorModulo({ error }: { error: unknown }) {
  return (
    <div className="mx-auto flex max-w-md flex-col items-center gap-2 py-16 text-center">
      <h2 className="text-lg font-medium text-crit">No pudimos cargar</h2>
      <p className="text-sm text-fg-secondary">
        {error instanceof Error ? error.message : 'Error desconocido'}
      </p>
    </div>
  );
}
